import React, { useState } from 'react';
import { 
  Box, 
  Card, 
  Tabs, 
  Tab, 
  Typography, 
  Slider, 
  List, 
  ListItem, 
  ListItemText, 
  Divider
} from '@mui/material';

const GenerateCount = () => {
  // カテゴリデータ
  const [categories, setCategories] = useState([
    {
      id: 1,
      name: "① SD1.5, SDXL",
      totalCredits: 150,
      usedCredits: 45,
      color: "#2196f3" // MUI Blue
    },
    {
      id: 2,
      name: "② Imagen3",
      totalCredits: 80,
      usedCredits: 20,
      color: "#4caf50" // MUI Green
    }
  ]);
  
  // 状態管理
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const selectedCategory = categories[selectedTabIndex];
  
  // 円の設定
  const radius = 80;
  const strokeWidth = 18;
  const svgSize = 240;
  const center = svgSize / 2;
  
  // カテゴリ情報の操作関数
  const updateCategoryUsage = (index, newRemainingCredits) => {
    const newCategories = [...categories];
    newCategories[index] = {
      ...newCategories[index],
      usedCredits: newCategories[index].totalCredits - newRemainingCredits
    };
    setCategories(newCategories);
  };
  
  const updateCategoryTotal = (index, newTotalCredits) => {
    const newCategories = [...categories];
    const usedCredits = Math.min(newCategories[index].usedCredits, newTotalCredits);
    newCategories[index] = {
      ...newCategories[index],
      totalCredits: newTotalCredits,
      usedCredits: usedCredits
    };
    setCategories(newCategories);
  };
  
  // グレーの背景円を描画
  const grayBackground = () => {
    return (
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="transparent"
        stroke="#e0e0e0" // MUI Gray 300
        strokeWidth={strokeWidth}
      />
    );
  };
  
  // カテゴリの円弧を描画
  const categoryArc = (category) => {
    const remainingCredits = category.totalCredits - category.usedCredits;
    const percentage = (remainingCredits / category.totalCredits) * 100;
    const circumference = 2 * Math.PI * radius;
    const dashArray = circumference;
    const dashOffset = circumference * (1 - percentage / 100);
    
    return (
      <circle
        key={category.id}
        cx={center}
        cy={center}
        r={radius}
        fill="transparent"
        stroke={category.color}
        strokeWidth={strokeWidth}
        strokeDasharray={dashArray}
        strokeDashoffset={dashOffset}
        strokeLinecap="round"
      />
    );
  };
  
  const handleTabChange = (event, newValue) => {
    setSelectedTabIndex(newValue);
  };
  
  const handleRemainingChange = (event, newValue) => {
    updateCategoryUsage(selectedTabIndex, newValue);
  };
  
  const handleTotalChange = (event, newValue) => {
    updateCategoryTotal(selectedTabIndex, newValue);
  };
  
  const handleCategoryClick = (index) => {
    setSelectedTabIndex(index);
  };

  // 残りクレジット計算
  const getRemainingCredits = (category) => {
    return category.totalCredits - category.usedCredits;
  };
  
  // 残りクレジットのパーセンテージ計算
  const getRemainingPercentage = (category) => {
    return (getRemainingCredits(category) / category.totalCredits) * 100;
  };
  
  // 使用済みクレジットのパーセンテージ計算
  const getUsedPercentage = (category) => {
    return (category.usedCredits / category.totalCredits) * 100;
  };
  
  const PercentageCircle = ({ color, value }) => (
    <Box 
      sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '50%',
        bgcolor: `${color}20`,
        color: color,
        fontWeight: 'bold',
        fontSize: '1.1rem'
      }}
    >
      {value}%
    </Box>
  );

  const CategoryIndicator = ({ color }) => (
    <Box 
      sx={{ 
        width: 12, 
        height: 12, 
        borderRadius: '50%', 
        mr: 1, 
        bgcolor: color 
      }}
    />
  );
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 500, mb: 2 }}>
        <Tabs
          value={selectedTabIndex}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant="fullWidth"
        >
          {categories.map((category, index) => (
            <Tab key={category.id} label={category.name} />
          ))}
        </Tabs>
        
        {/* メインの円グラフ表示 */}
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ position: 'relative', mx: 'auto', width: svgSize, height: svgSize }}>
            <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`} style={{ transform: 'rotate(-90deg)' }}>
              {grayBackground()}
              {categoryArc(selectedCategory)}
            </svg>
            
            {/* 中央のテキスト表示 */}
            <Box 
              sx={{ 
                position: 'absolute', 
                inset: 0, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 300 }}>
                {getRemainingCredits(selectedCategory)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                残り回数
              </Typography>
            </Box>
          </Box>
          
          {/* 選択中のカテゴリ情報 */}
          <Box sx={{ width: '100%', maxWidth: 300, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CategoryIndicator color={selectedCategory.color} />
              <Typography variant="body2">
                残り: {getRemainingCredits(selectedCategory)} 回
                ({getRemainingPercentage(selectedCategory).toFixed(1)}%)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CategoryIndicator color="#e0e0e0" />
              <Typography variant="body2">
                使用済み: {selectedCategory.usedCredits} 回
                ({getUsedPercentage(selectedCategory).toFixed(1)}%)
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>
      
      {/* 調整スライダー */}
      <Card sx={{ width: '100%', maxWidth: 500, p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {selectedCategory.name}の設定
        </Typography>
        
        <Box sx={{ width: '100%', maxWidth: 'xs', mb: 3 }}>
          <Typography id="remaining-slider" gutterBottom>
            {selectedCategory.name}の残り回数を調整:
          </Typography>
          <Slider
            aria-labelledby="remaining-slider"
            min={0}
            max={selectedCategory.totalCredits}
            value={getRemainingCredits(selectedCategory)}
            onChange={handleRemainingChange}
            valueLabelDisplay="auto"
            color="primary"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">0</Typography>
            <Typography variant="caption" color="text.secondary">{selectedCategory.totalCredits}</Typography>
          </Box>
        </Box>
        
        <Box sx={{ width: '100%', maxWidth: 'xs' }}>
          <Typography id="total-slider" gutterBottom>
            {selectedCategory.name}の合計回数を調整:
          </Typography>
          <Slider
            aria-labelledby="total-slider"
            min={selectedCategory.usedCredits}
            max={300}
            value={selectedCategory.totalCredits}
            onChange={handleTotalChange}
            valueLabelDisplay="auto"
            color="secondary"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">{selectedCategory.usedCredits}</Typography>
            <Typography variant="caption" color="text.secondary">300</Typography>
          </Box>
        </Box>
      </Card>
      
      {/* すべてのカテゴリの一覧表示 */}
      <Card sx={{ width: '100%', maxWidth: 500, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          すべてのモデルタイプの残り回数
        </Typography>
        
        <List disablePadding>
          {categories.map((category, index) => {
            const remainingCredits = getRemainingCredits(category);
            const percentage = getRemainingPercentage(category);
            
            return (
              <React.Fragment key={category.id}>
                {index > 0 && <Divider />}
                <ListItem 
                  button
                  selected={selectedTabIndex === index}
                  onClick={() => handleCategoryClick(index)}
                  sx={{ 
                    mb: 1, 
                    borderLeft: '4px solid',
                    borderLeftColor: category.color,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      bgcolor: `${category.color}10`,
                    }
                  }}
                >
                  <ListItemText
                    primary={category.name}
                    secondary={`残り ${remainingCredits}/${category.totalCredits} 回`}
                  />
                  <PercentageCircle 
                    color={category.color} 
                    value={percentage.toFixed(0)} 
                  />
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </Card>
    </Box>
  );
};

export default GenerateCount;